// @ts-nocheck
import { createRouter, createWebHistory } from "vue-router";

import { getAccessToken } from "@/helpers/utils";
import { B2bPortalRight } from "@/enums/B2bPortalRight";
import { preloadCoreData, waitForCoreData } from "@modules/app/composables/usePreloader";
import { CONFIG } from "@/const/config";

/**
 * Decode a JWT and check whether it is still valid (not expired).
 * Returns false if the token is missing, malformed, or expired.
 */
function isTokenValid(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return true; // no expiry claim — treat as valid
    // exp is in seconds, Date.now() in ms
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

const user = localStorage.getItem("user");
const defaultRedirectUrl = user ? "new-login" : "new-registration";

const routes = [
  {
    path: "/app/registration",
    name: "new-registration",
    meta: { layout: "empty", registrationStep: 1 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/phone",
    name: "new-registration-phone",
    meta: { layout: "empty", registrationStep: 2 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/personal",
    name: "new-registration-personal",
    meta: { layout: "empty", registrationStep: 3 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/company",
    name: "new-registration-company",
    meta: { layout: "empty", registrationStep: 4 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/address",
    name: "new-registration-address",
    meta: { layout: "empty", registrationStep: 5 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/structure",
    name: "new-registration-structure",
    meta: { layout: "empty", registrationStep: 6 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/status",
    name: "new-registration-status",
    meta: { layout: "empty", registrationStep: 7 },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/NewRegistration.vue"),
  },
  {
    path: "/app/registration/verification",
    name: "verification",
    meta: { layout: "auth" },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/verify/VerifyIdentity.vue"),
  },
  {
    path: "/app/registration/verification/liveness",
    name: "liveness",
    meta: { layout: "auth" },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/liveness/Liveness.vue"),
  },
  {
    path: "/success-verification-confirmed",
    name: "verification-result",
    meta: { layout: "auth" },
    component: () => import(/* webpackChunkName: "registration" */ "@modules/auth/views/registration/Result.vue"),
  },
  {
    path: "/login",
    redirect: (to: any) => ({ path: "/app/login", query: to.query }),
  },
  {
    path: "/forgot-password",
    redirect: (to: any) => ({ path: "/app/forgot-password", query: to.query }),
  },
  // New Modern Layout Routes
  {
    path: "/app/dashboard",
    name: "new-dashboard",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "accounts" */ "@modules/accounts/views/Dashboard.vue"),
  },
  {
    path: "/app/accounts",
    name: "new-accounts",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "accounts" */ "@modules/accounts/views/Accounts.vue"),
  },
  {
    path: "/app/add-money",
    name: "new-add-money",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "accounts" */ "@modules/accounts/views/AddMoney.vue"),
  },
  {
    path: "/app/account/:id",
    name: "new-account-detail",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "accounts" */ "@modules/accounts/views/AccountDetail.vue"),
  },
  {
    path: "/app/payments",
    name: "new-payments",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/Payments.vue"),
  },
  {
    path: "/app/trades",
    name: "new-trades",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "trading" */ "@modules/trading/views/MyTrades.vue"),
  },
  {
    path: "/app/chat",
    name: "new-chat",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "chat" */ "@modules/app/views/chat/Chat.vue"),
  },
  {
    path: "/app/users-management",
    name: "users-management",
    meta: { layout: "floating-slim", permissions: [B2bPortalRight.UsersView] },
    component: () => import(/* webpackChunkName: "users" */ "@modules/users/views/Users.vue"),
  },
  {
    path: "/app/transaction/:id",
    name: "transaction-detail",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/TransactionDetail.vue"),
  },
  {
    path: "/app/cards",
    name: "new-cards",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "cards" */ "@modules/cards/views/Cards.vue"),
  },
  {
    path: "/app/cards/new-card",
    name: "new-card-order",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "cards" */ "@modules/cards/views/Cards.vue"),
  },
  {
    path: "/app/cards/get-card",
    name: "new-get-card",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "cards" */ "@modules/cards/views/GetCard.vue"),
  },
  {
    path: "/app/exchange",
    name: "new-exchange",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "exchange" */ "@modules/exchange/views/Exchange.vue"),
  },
  {
    path: "/app/crypto",
    name: "new-crypto",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/Crypto.vue"),
  },
  {
    path: "/app/crypto/trading/:pair?",
    name: "new-crypto-trading",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "trading" */ "@modules/trading/views/CryptoTrading.vue"),
  },
  {
    path: "/app/crypto/:symbol",
    name: "new-crypto-detail",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/CryptoDetail.vue"),
  },
  {
    path: "/app/crypto/:symbol/network",
    name: "new-crypto-network",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/CryptoSelectNetwork.vue"),
  },
  {
    path: "/app/send",
    name: "new-send-money",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/SendMoney.vue"),
  },
  {
    path: "/app/crypto/:symbol/deposit",
    name: "new-crypto-deposit",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/CryptoDeposit.vue"),
  },
  {
    path: "/app/crypto/:symbol/send",
    name: "new-crypto-send",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "payments" */ "@modules/payments/views/CryptoSend.vue"),
  },
  {
    path: "/app/renting/:tab?",
    name: "renting",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  {
    path: "/app/renting/:tab/product/:productName",
    name: "renting-product",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  {
    path: "/app/renting/:tab/product/:productName/amount",
    name: "renting-product-amount",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  {
    path: "/app/renting/:tab/product/:productName/rewards",
    name: "renting-product-rewards",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  {
    path: "/app/renting/:tab/product/:productName/confirm",
    name: "renting-product-confirm",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  {
    path: "/app/renting/:tab/product/:productName/created",
    name: "renting-product-created",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "new" */ "@modules/renting/views/Renting.vue"),
  },
  // Loans module routes — gated by VUE_APP_LOAN_MODULE_ALLOWED
  ...(CONFIG.LOAN_MODULE_ALLOWED ? [
    {
      path: "/app/loans",
      name: "loans-catalog",
      meta: { layout: "floating-slim" },
      component: () => import(/* webpackChunkName: "loans" */ "@modules/loans/views/LoanCatalog.vue"),
    },
    {
      path: "/app/loans/active",
      name: "loans-active",
      meta: { layout: "floating-slim" },
      component: () => import(/* webpackChunkName: "loans" */ "@modules/loans/views/ActiveLoans.vue"),
    },
    {
      path: "/app/loans/:loanId",
      name: "loan-detail",
      meta: { layout: "floating-slim" },
      component: () => import(/* webpackChunkName: "loans" */ "@modules/loans/views/LoanDetail.vue"),
    },
    {
      path: "/app/loans/:loanId/repay",
      name: "loan-repay",
      meta: { layout: "floating-slim" },
      component: () => import(/* webpackChunkName: "loans" */ "@modules/loans/views/RepayLoan.vue"),
    },
    {
      path: "/app/loans/:loanId/add-collateral",
      name: "loan-add-collateral",
      meta: { layout: "floating-slim" },
      component: () => import(/* webpackChunkName: "loans" */ "@modules/loans/views/AddCollateral.vue"),
    },
  ] : []),
  {
    path: "/app/settings",
    name: "new-settings",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "settings" */ "@modules/settings/views/Settings.vue"),
  },
  {
    path: "/app/help-center",
    name: "help-center",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "help" */ "@modules/app/views/help/HelpCenter.vue"),
  },
  {
    path: "/app/help-center/:categoryId",
    name: "help-category",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "help" */ "@modules/app/views/help/HelpCategory.vue"),
  },
  {
    path: "/app/help-center/:categoryId/:articleId",
    name: "help-article",
    meta: { layout: "floating-slim" },
    component: () => import(/* webpackChunkName: "help" */ "@modules/app/views/help/HelpArticle.vue"),
  },
  // template, notifications, typography routes removed — require primevue (not yet ported)
  {
    path: "/app/login",
    name: "new-login",
    meta: { layout: "empty" },
    component: () => import(/* webpackChunkName: "auth-new" */ "@modules/auth/views/NewLogin.vue"),
  },
  {
    path: "/app/forgot-password",
    name: "new-forgot-password",
    meta: { layout: "empty" },
    component: () => import(/* webpackChunkName: "auth-new" */ "@modules/auth/views/ForgotPassword.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: () => ({ name: defaultRedirectUrl }),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there is a saved position (e.g. from back button), return to it
    if (savedPosition) {
      return savedPosition;
    }
    // Otherwise scroll to top
    return { top: 0, left: 0 };
  }
});

// Track whether preload has been triggered this session
let preloadStarted = false;

router.beforeEach(async (to, from, next) => {
  const publicRoutes = [
    '/app/registration',
    '/app/login',
    '/app/forgot-password',
    '/success-verification-confirmed',
  ];
  const isPublicRoute = publicRoutes.includes(to.path) || to.path.startsWith('/app/registration/');

  const token = getAccessToken();

  if (!isPublicRoute && !isTokenValid(token)) {
    next('/app/login');
    return;
  }

  // For authenticated routes: trigger preload on first navigation and wait for Phase 1
  if (!isPublicRoute && isTokenValid(token)) {
    if (!preloadStarted) {
      preloadStarted = true;
      preloadCoreData();
    }
    // Wait for accounts to be fetched before navigating to dashboard/accounts
    // This prevents the flash of wrong-state content
    const waitRoutes = ['new-dashboard', 'new-accounts'];
    if (waitRoutes.includes(to.name as string)) {
      await waitForCoreData();
    }
  }

  // Check route-level permissions
  const requiredPermissions = to.meta.permissions || [];
  if (requiredPermissions.length > 0) {
    // Read permissions from Pinia stores with sessionStorage fallback
    let userPermissions = [];
    try {
      const { useAuthStore } = require('@modules/auth/stores/auth');
      const authStore = useAuthStore();
      if (authStore.permissions?.rights) {
        userPermissions = [...authStore.permissions.rights];
      }
    } catch (e) {
      // Fallback to sessionStorage
    }

    // Fallback: also check sessionStorage if Pinia had no permissions
    if (userPermissions.length === 0) {
      try {
        const savedPerms = sessionStorage.getItem('user_permissions');
        if (savedPerms) {
          const parsed = JSON.parse(savedPerms);
          userPermissions = parsed?.rights || [];
        }
      } catch (e) {
        // Allow access on parse failure
      }
    }

    // Check registration store permissions for registration routes
    if (to.path.startsWith('/app/registration')) {
      try {
        const { useRegistrationStore } = require('@modules/auth/stores/registration');
        const registrationStore = useRegistrationStore();
        if (registrationStore.permissions) {
          userPermissions.push(...registrationStore.permissions);
        }
      } catch (e) {
        // Ignore
      }
    }

    const hasPermission = userPermissions.some(p => requiredPermissions.includes(p));
    if (!hasPermission) {
      next('/access-denied');
      return;
    }
  }
  next();
});

export default router;
